import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import * as Sentry from "@sentry/react";

export interface ProfileActivity {
  id: string;
  profile_user_id: string;
  action_type: 'update' | 'access' | 'role_change' | 'upload' | 'note_add' | 'auth';
  actor_user_id: string;
  field_name?: string;
  old_value?: any;
  new_value?: any;
  metadata: any;
  is_sensitive: boolean;
  created_at: string;
  organization_id?: string;
}

export interface ProfileSnapshot {
  id: string;
  profile_user_id: string;
  snapshot_data: any;
  created_by: string;
  created_at: string;
  version_number: number;
  change_summary?: string;
}

// Hook to get profile activity logs
export function useProfileActivity(profileUserId?: string, limit = 50) {
  return useQuery({
    queryKey: ['profile-activity', profileUserId],
    queryFn: async () => {
      let query = supabase
        .from('profile_activity_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (profileUserId) {
        query = query.eq('profile_user_id', profileUserId);
      }

      const { data, error } = await query;

      if (error) {
        Sentry.captureException(error, {
          tags: { context: 'activity_error', operation: 'profile_activity', action: 'fetch' },
          extra: { message: 'Error fetching profile activity', profileUserId }
        });
        throw error;
      }

      return data as ProfileActivity[];
    },
    enabled: true,
  });
}

// Hook to get profile snapshots (versions)
export function useProfileSnapshots(profileUserId: string) {
  return useQuery({
    queryKey: ['profile-snapshots', profileUserId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profile_snapshots')
        .select('*')
        .eq('profile_user_id', profileUserId)
        .order('version_number', { ascending: false });

      if (error) {
        Sentry.captureException(error, {
          tags: { context: 'activity_error', operation: 'profile_activity', action: 'fetch_snapshots' },
          extra: { message: 'Error fetching profile snapshots', profileUserId }
        });
        throw error;
      }

      return data as ProfileSnapshot[];
    },
    enabled: !!profileUserId,
  });
}

// Hook to log profile activity
export function useLogProfileActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      profileUserId,
      actionType,
      fieldName,
      oldValue,
      newValue,
      metadata = {},
      isSensitive = false,
    }: {
      profileUserId: string;
      actionType: string;
      fieldName?: string;
      oldValue?: any;
      newValue?: any;
      metadata?: any;
      isSensitive?: boolean;
    }) => {
      const { error } = await supabase.rpc('log_profile_activity', {
        p_profile_user_id: profileUserId,
        p_action_type: actionType,
        p_field_name: fieldName,
        p_old_value: oldValue ? JSON.stringify(oldValue) : null,
        p_new_value: newValue ? JSON.stringify(newValue) : null,
        p_metadata: metadata,
        p_is_sensitive: isSensitive,
      });

      if (error) {
        Sentry.captureException(error, {
          tags: { context: 'activity_error', operation: 'profile_activity', action: 'log' },
          extra: { message: 'Error logging profile activity', profileUserId }
        });
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile-activity'] });
    },
  });
}

// Hook to create profile snapshot
export function useCreateProfileSnapshot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      profileUserId,
      changeSummary,
    }: {
      profileUserId: string;
      changeSummary?: string;
    }) => {
      const { data, error } = await supabase.rpc('create_profile_snapshot', {
        p_profile_user_id: profileUserId,
        p_change_summary: changeSummary,
      });

      if (error) {
        Sentry.captureException(error, {
          tags: { context: 'activity_error', operation: 'profile_activity', action: 'create_snapshot' },
          extra: { message: 'Error creating profile snapshot', profileUserId }
        });
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile-snapshots'] });
      toast.success('Profile snapshot created successfully');
    },
  });
}

// Hook to restore profile from snapshot
export function useRestoreProfileSnapshot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      snapshotId,
      profileUserId,
    }: {
      snapshotId: string;
      profileUserId: string;
    }) => {
      // Get snapshot data
      const { data: snapshot, error: snapshotError } = await supabase
        .from('profile_snapshots')
        .select('snapshot_data')
        .eq('id', snapshotId)
        .single();

      if (snapshotError) throw snapshotError;

      // Remove metadata fields that shouldn't be restored
      const snapshotData = snapshot.snapshot_data as any;
      const { id, user_id, created_at, updated_at, ...restoreData } = snapshotData;

      // Update profile with snapshot data
      const { error: updateError } = await supabase
        .from('profiles')
        .update(restoreData)
        .eq('user_id', profileUserId);

      if (updateError) throw updateError;

      return snapshot;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profiles'] });
      queryClient.invalidateQueries({ queryKey: ['profile-activity'] });
      toast.success('Profile restored from snapshot successfully');
    },
    onError: (error) => {
      Sentry.captureException(error, {
        tags: { context: 'activity_error', operation: 'profile_activity', action: 'restore_snapshot' },
        extra: { message: 'Error restoring profile snapshot' }
      });
      toast.error('Failed to restore profile snapshot');
    },
  });
}

// Format activity for display
export function formatActivityMessage(activity: ProfileActivity): string {
  const { action_type, field_name, old_value, new_value } = activity;

  switch (action_type) {
    case 'update':
      if (field_name) {
        return `Updated ${field_name.replace('_', ' ')}`;
      }
      return 'Updated profile';
    case 'access':
      return 'Accessed profile';
    case 'role_change':
      return `Role changed from ${old_value} to ${new_value}`;
    case 'upload':
      return `Uploaded ${field_name || 'file'}`;
    case 'note_add':
      return 'Added note';
    case 'auth':
      return 'Authentication event';
    default:
      return `Performed ${action_type}`;
  }
}